import { apiFetch } from '../../_services/apiFetch';
import { ApprovalDecisionActions } from '../../_components/Shared/ApprovalDecisionActions';
import React from 'react';
import { Alert, Button, Form, FormGroup, FormControl, Row, Col } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import config from '../../../config';
import utils from '../../utils';
import moment from 'moment';
import { handleFocusInScrollField, INVENTORY_REPORT_CARD_SCROLL_STYLE } from './invKeyboardScroll';

function statusLabelScrap(status, rejectionReason) {
    const st = String(status || '').toLowerCase();
    if (st === 'rejected') return `Rejected. ${rejectionReason || 'No reason provided'}`;
    if (st === 'approved' || st === 'confirmed') return 'Approved';
    if (st === 'pending_approval' || st === 'pending_confirmation' || st === 'pending') {
        return 'Pending Approval';
    }
    return status || 'Pending';
}

class ScrappingSummaryReport extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            id: null,
            reportNo: '',
            refNo: '',
            processDate: '',
            inspection: '',
            materialKind: '',
            partNo: '',
            description: '',
            lotNo: '',
            quantity: 0,
            locationCode: '',
            locationDescription: '',
            area: '',
            reason: '',
            status: '',
            submittedBy: '',
            submittedDateTime: '',
            approvedBy: '',
            approvedDateTime: '',
            rejectedBy: '',
            rejectedDateTime: '',
            rejectionReason: '',
            photoUrl: '',
            loading: true,
            error: null,
        };
    }

    componentDidMount() {
        this.loadScrapData();
    }

    loadScrapData = async (decisionSaved = false) => {
        const { id } = this.props.params || {};
        if (!id) return;

        try {
            const response = await apiFetch(`${config.apiUrl}/getInventoryApprovalRecord?id=${id}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error('Failed to load approval record');

            const data = await response.json();
            if (!data) throw new Error('Approval record not found');

            const reportNo =
                data.createdAt && data.id
                    ? moment(data.createdAt).format('YYYYMMDD') + 'S' + data.id
                    : data.id || '';
            const status = data.status || 'pending_approval';
            const st = String(status).toLowerCase();

            this.setState({
                error: null,
                id: data.id,
                canApprove: data.canApprove === true,
                approvalLevel: data.approvalLevel,
                reportNo,
                refNo: data.transactionId || '',
                processDate: data.createdAt ? moment(data.createdAt).format('DD/MM/YYYY') : '',
                inspection:
                    st === 'approved' || st === 'confirmed'
                        ? 'Yes'
                        : st === 'rejected'
                          ? 'No'
                          : 'Pending',
                materialKind: data.materialKind || data.transferType || '',
                partNo: data.partNo || '',
                description: data.description || '',
                lotNo: data.lotNo || '',
                quantity: data.quantity || 0,
                locationCode: data.locationCode || data.fromLocationCode || '',
                locationDescription:
                    data.locationDescription || data.fromLocationDescription || '',
                area: data.area || '',
                reason: data.reason || data.scrapReason || '',
                status: statusLabelScrap(status, data.rejectionReason),
                submittedBy: data.createdBy || '',
                submittedDateTime: data.createdAt
                    ? moment(data.createdAt).format('DD/MM/YYYY HH:mm:ss')
                    : '',
                approvedBy: data.approvedByName || '',
                approvedDateTime: data.approvedAt
                    ? moment(data.approvedAt).format('DD/MM/YYYY HH:mm:ss')
                    : '',
                rejectedBy: data.rejectedByName || '',
                rejectedDateTime: data.rejectedAt
                    ? moment(data.rejectedAt).format('DD/MM/YYYY HH:mm:ss')
                    : '',
                rejectionReason: data.rejectionReason || '',
                photoUrl: data.photoUrl || '',
                loading: false,
            });
        } catch (error) {
            console.error('Error loading scrap data:', error);
            this.setState({
                loading: false,
                canApprove: false,
                error: decisionSaved === true
                    ? 'Decision saved, but latest details could not be refreshed. Reload the page.'
                    : 'Approval record could not be loaded. Please refresh the page.',
            });
        }
    };

    exportExcel = () => {
        const partNo = this.state.partNo || 'unknown';
        const timestamp = this.state.processDate || new Date().toLocaleString();
        utils.tableToExcel(
            'scrapping-report-table',
            'Report',
            'Scrapping_report_' + partNo + '_' + timestamp + '.xls'
        );
    };

    render() {
        if (this.state.loading) {
            return <div>Loading...</div>;
        }

        return (
            <div
                className="container"
                style={{ marginTop: '20px', backgroundColor: '#f5f5f5', padding: '20px' }}
            >
                {this.state.error && <Alert variant="warning">{this.state.error}</Alert>}
                <div
                    className="no-print"
                    style={{
                        marginBottom: '20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <Link to="/scrapping-summary" className="btn btn-outline-secondary">Back</Link>
                        <h4 className="mb-0">Scrapping Individual Report</h4>
                    </div>
                    <ApprovalDecisionActions
                        recordType={this.state.materialKind === 'FG' ? 'inventory_scrap_fg' : 'inventory_scrap_rm'}
                        recordId={this.state.id}
                        currentLevel={this.state.approvalLevel}
                        canApprove={this.state.canApprove}
                        canReject={this.state.canApprove}
                        onDecision={() => this.loadScrapData(true)}
                    />
                    <div>
                        <Button bsStyle="primary" onClick={this.exportExcel}>
                            Export
                        </Button>
                    </div>
                </div>

                <div style={INVENTORY_REPORT_CARD_SCROLL_STYLE} onFocusCapture={handleFocusInScrollField}>
                    <div
                        style={{
                            marginBottom: '15px',
                            borderBottom: '2px solid #ddd',
                            paddingBottom: '10px',
                        }}
                    >
                        <span
                            style={{
                                padding: '5px 15px',
                                backgroundColor: '#8e44ad',
                                color: '#fff',
                                borderRadius: '3px',
                                fontSize: '14px',
                            }}
                        >
                            Scrapping Report
                        </span>
                    </div>

                    <table id="scrapping-report-table" style={{ width: '100%' }}>
                        <tbody>
                            <tr>
                                <td colSpan="2" style={{ padding: 0, border: 'none' }}>
                                    <Row>
                                        <Col md={6} className="report-content">
                                            <Form>
                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Report No:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.reportNo}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Ref No:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.refNo}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Submitted Date:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.submittedDateTime}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Type:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.materialKind}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Part No:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.partNo}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Description:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.description}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Lot No:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.lotNo}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Quantity:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.quantity}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Location:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={
                                                                    this.state.locationCode +
                                                                    (this.state.locationDescription
                                                                        ? ` - ${this.state.locationDescription}`
                                                                        : '')
                                                                }
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Area:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.area}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Row>
                                                        <Col md={4}>
                                                            <strong>Reason:</strong>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormControl
                                                                as="textarea"
                                                                rows={3}
                                                                value={this.state.reason}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>
                                            </Form>
                                        </Col>

                                        <Col md={6} className="report-content page-break">
                                            <Form>
                                                <h5
                                                    style={{
                                                        marginTop: '0',
                                                        marginBottom: '15px',
                                                    }}
                                                >
                                                    Status:
                                                </h5>
                                                <FormGroup>
                                                    <FormControl
                                                        type="text"
                                                        value={this.state.status}
                                                        readOnly
                                                        style={{ backgroundColor: '#f9f9f9' }}
                                                    />
                                                </FormGroup>

                                                <h5
                                                    style={{
                                                        marginTop: '20px',
                                                        marginBottom: '15px',
                                                    }}
                                                >
                                                    Submitted by:
                                                </h5>
                                                <FormGroup>
                                                    <FormControl
                                                        type="text"
                                                        value={this.state.submittedBy}
                                                        readOnly
                                                        style={{ backgroundColor: '#f9f9f9' }}
                                                    />
                                                </FormGroup>

                                                <h5
                                                    style={{
                                                        marginTop: '20px',
                                                        marginBottom: '15px',
                                                    }}
                                                >
                                                    Approved by:
                                                </h5>
                                                <FormGroup>
                                                    <FormControl
                                                        type="text"
                                                        value={this.state.approvedBy}
                                                        readOnly
                                                        style={{ backgroundColor: '#f9f9f9' }}
                                                    />
                                                </FormGroup>

                                                <h5
                                                    style={{
                                                        marginTop: '20px',
                                                        marginBottom: '15px',
                                                    }}
                                                >
                                                    Approved date &amp; time:
                                                </h5>
                                                <FormGroup>
                                                    <FormControl
                                                        type="text"
                                                        value={this.state.approvedDateTime}
                                                        readOnly
                                                        style={{ backgroundColor: '#f9f9f9' }}
                                                    />
                                                </FormGroup>

                                                {this.state.rejectedBy ? (
                                                    <>
                                                        <h5
                                                            style={{
                                                                marginTop: '20px',
                                                                marginBottom: '15px',
                                                            }}
                                                        >
                                                            Rejected by:
                                                        </h5>
                                                        <FormGroup>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.rejectedBy}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </FormGroup>
                                                        <h5
                                                            style={{
                                                                marginTop: '20px',
                                                                marginBottom: '15px',
                                                            }}
                                                        >
                                                            Rejected date &amp; time:
                                                        </h5>
                                                        <FormGroup>
                                                            <FormControl
                                                                type="text"
                                                                value={this.state.rejectedDateTime}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </FormGroup>
                                                    </>
                                                ) : null}

                                                {this.state.rejectionReason ? (
                                                    <>
                                                        <h5
                                                            style={{
                                                                marginTop: '20px',
                                                                marginBottom: '15px',
                                                            }}
                                                        >
                                                            Rejection reason:
                                                        </h5>
                                                        <FormGroup>
                                                            <FormControl
                                                                as="textarea"
                                                                rows={3}
                                                                value={this.state.rejectionReason}
                                                                readOnly
                                                                style={{ backgroundColor: '#f9f9f9' }}
                                                            />
                                                        </FormGroup>
                                                    </>
                                                ) : null}

                                                <h5
                                                    style={{
                                                        marginTop: '30px',
                                                        marginBottom: '15px',
                                                    }}
                                                >
                                                    Photo:
                                                </h5>
                                                <FormGroup>
                                                    {this.state.photoUrl ? (
                                                        <div
                                                            style={{
                                                                border: '1px solid #ddd',
                                                                padding: '10px',
                                                                textAlign: 'center',
                                                                backgroundColor: '#f9f9f9',
                                                            }}
                                                        >
                                                            <img
                                                                src={this.state.photoUrl}
                                                                alt="Scrap Photo"
                                                                style={{
                                                                    maxWidth: '100%',
                                                                    maxHeight: '300px',
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            style={{
                                                                border: '1px solid #ddd',
                                                                padding: '40px',
                                                                textAlign: 'center',
                                                                backgroundColor: '#f9f9f9',
                                                                color: '#999',
                                                            }}
                                                        >
                                                            No photo available
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Form>
                                        </Col>
                                    </Row>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <style>{`
                    @media print {
                        body {
                            margin: 0;
                            padding: 0;
                        }
                        .container {
                            width: 100% !important;
                            max-width: 100% !important;
                            margin: 0 !important;
                            padding: 10px !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                        .page-break {
                            page-break-after: always;
                        }
                        .report-content {
                            page-break-inside: avoid;
                        }
                    }
                `}</style>
            </div>
        );
    }
}

function withParams(Component) {
    return (props) => {
        const params = useParams();
        return <Component {...props} params={params} />;
    };
}

const ScrappingSummaryReportWithParams = withParams(ScrappingSummaryReport);
export { ScrappingSummaryReportWithParams as ScrappingSummaryReport };
